/**
 * PublicLeagueLeaderboardPage — /l/:slug/leaders
 * ─────────────────────────────────────────────────────────────────
 * RESTYLED in the league-module cosmetic pass (2026-06-09) to the
 * pubUi design kit: podium cards for the top three, a modern table
 * for the rest, season picker in the section header. Privacy logic
 * unchanged (initials unless opted-in; minors always initials).
 *
 * 2026-09-28 — Regular season / Playoffs / All games switch (shown when
 * the season has a playoff bracket). The API defaults to regular season.
 */

import { useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import {
  container, Section, Card, EmptyState,
  INK, MUTED, LINE, SURFACE, accentText,
} from './pubUi.jsx';

export default function PublicLeagueLeaderboardPage() {
  const { league, currentSeason, accent } = useOutletContext();
  const { slug } = useParams();

  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState(currentSeason?.id || '');
  const [players, setPlayers] = useState(null);
  const [scope, setScope] = useState('regular');
  const [hasPlayoffs, setHasPlayoffs] = useState(false);

  useEffect(() => { document.title = `Stat leaders — ${league.name}`; }, [league.name]);

  useEffect(() => {
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/seasons`)
      .then(({ data }) => {
        const list = data?.seasons || [];
        setSeasons(list);
        setSeasonId(prev => prev || list[0]?.id || '');
      })
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!seasonId) return;
    let cancelled = false;
    setPlayers(null);
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/seasons/${seasonId}/leaderboard?scope=${scope}`)
      .then(({ data }) => {
        if (cancelled) return;
        setPlayers(data?.players || []);
        setHasPlayoffs(data?.has_playoffs === true);
      })
      .catch(() => { if (!cancelled) setPlayers([]); });
    return () => { cancelled = true; };
  }, [slug, seasonId, scope]);

  const podium = (players || []).slice(0, 3);
  const rest = (players || []).slice(3);

  return (
    <div style={{ ...container, paddingTop: 36, maxWidth: 960 }}>
      <Section
        eyebrow={seasons.find(s => s.id === seasonId)?.name || ''}
        title="Stat leaders"
        action={seasons.length > 1 && (
          <select value={seasonId} onChange={(e) => { setSeasonId(e.target.value); setScope('regular'); }}
            style={{ padding: '10px 14px', borderRadius: 12, border: `1px solid ${LINE}`, background: '#fff', fontSize: 14, fontWeight: 600 }}>
            {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}>

        {hasPlayoffs && (
          <div role="group" aria-label="Games counted" style={{ display: 'inline-flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
            {[['regular', 'Regular season'], ['playoffs', 'Playoffs'], ['all', 'All games']].map(([id, text]) => (
              <button key={id} type="button" onClick={() => setScope(id)} aria-pressed={scope === id} style={{
                padding: '7px 14px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${scope === id ? accent : LINE}`,
                background: scope === id ? accent : '#fff',
                color: scope === id ? accentText(accent) : INK,
              }}>{text}</button>
            ))}
          </div>
        )}
        {players === null && <div style={{ color: MUTED }}>Loading leaders…</div>}
        {players?.length === 0 && (
          <EmptyState icon="📈">No scoring data yet for this season.</EmptyState>
        )}

        {/* ── Podium — top three ──────────────────────────────── */}
        {podium.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 20 }}>
            {podium.map((p, i) => (
              <Card key={`${p.player_uid || 'x'}-${i}`} style={{
                padding: '18px 20px', position: 'relative', overflow: 'hidden',
                border: i === 0 ? `2px solid ${accent}` : `1px solid ${LINE}`,
              }}>
                <div style={{
                  position: 'absolute', top: 12, right: 14, fontSize: 24,
                }}>
                  {['🥇', '🥈', '🥉'][i]}
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: MUTED, marginBottom: 6 }}>
                  #{p.rank}
                </div>
                <div style={{ fontWeight: 900, fontSize: 18, color: INK, marginBottom: 2 }}>
                  {p.name}
                  {p.jersey_number != null && <span style={{ fontSize: 12, color: MUTED, fontWeight: 700 }}> #{p.jersey_number}</span>}
                </div>
                <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 12 }}>{p.team_name || '—'}</div>
                <div style={{ display: 'flex', gap: 14 }}>
                  <Stat label="G" value={p.goals} />
                  <Stat label="A" value={p.assists} />
                  <Stat label="PTS" value={p.points} strong accent={accent} />
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* ── The rest ────────────────────────────────────────── */}
        {rest.length > 0 && (
          <Card style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ color: MUTED, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    <th style={{ ...th, textAlign: 'left', paddingLeft: 20, width: 46 }}>#</th>
                    <th style={{ ...th, textAlign: 'left' }}>Player</th>
                    <th style={{ ...th, textAlign: 'left' }}>Team</th>
                    <th style={th}>G</th>
                    <th style={th}>A</th>
                    <th style={{ ...th, paddingRight: 20 }}>PTS</th>
                  </tr>
                </thead>
                <tbody>
                  {rest.map((p, i) => (
                    <tr key={`${p.player_uid || 'x'}-${i + 3}`} style={{ borderTop: `1px solid ${LINE}`, background: i % 2 ? SURFACE : '#fff' }}>
                      <td style={{ ...td, textAlign: 'left', paddingLeft: 20, color: MUTED, fontWeight: 800 }}>{p.rank}</td>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>
                        {p.name}
                        {p.jersey_number != null && <span style={{ fontSize: 11, color: MUTED, marginLeft: 6 }}>#{p.jersey_number}</span>}
                      </td>
                      <td style={{ ...td, textAlign: 'left', color: MUTED }}>{p.team_name || '—'}</td>
                      <td style={td}>{p.goals}</td>
                      <td style={td}>{p.assists}</td>
                      <td style={{ ...td, paddingRight: 20, fontWeight: 800, fontSize: 15 }}>{p.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {players?.length > 0 && (
          <p style={{ marginTop: 14, fontSize: 12, color: MUTED, maxWidth: 700 }}>
            Player names are shown as initials unless the player opted in to public display
            (and the league allows it). Minors always appear as initials.
          </p>
        )}
      </Section>
    </div>
  );
}

function Stat({ label, value, strong, accent }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontWeight: 900, fontSize: strong ? 24 : 19,
        color: strong ? (accent || INK) : INK, lineHeight: 1.1,
      }}>
        {value}
      </div>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: MUTED }}>{label}</div>
    </div>
  );
}

const th = { padding: '12px 10px', textAlign: 'center', fontWeight: 800 };
const td = { padding: '11px 10px', textAlign: 'center', color: INK };
